/**
 * TypeScript Type Definitions for Chess
 *
 * This file contains all type definitions and Zod schemas for the chess game.
 * Used for type safety and runtime validation.
 */

import { z } from 'zod';

// ============================================================================
// Enums
// ============================================================================

/**
 * Chess piece colors
 * PLAIN = White pieces (uppercase letters)
 * CHARCOAL = Black pieces (lowercase letters)
 */
export enum ChessColor {
  PLAIN = 'plain',      // White
  CHARCOAL = 'charcoal' // Black
}

/**
 * Game states
 */
export enum GameState {
  WAITING = 'waiting',      // Waiting for second player
  ONGOING = 'ongoing',      // Game in progress
  COMPLETED = 'completed'   // Game finished - check winnerId and endReason
}

/**
 * End game reasons
 */
export enum EndReason {
  CHECKMATE = 'checkmate',
  RESIGNATION = 'resignation',
  TIMEOUT = 'timeout',
  ABANDONED = 'abandoned'
}

// ============================================================================
// Zod Schemas (for runtime validation)
// ============================================================================

/**
 * Chess piece schema
 * Pieces are represented as single characters:
 * - Uppercase = White (PRNBQK)
 * - Lowercase = Black (prnbqk)
 * - null = Empty square
 */
export const ChessPieceSchema = z.union([
  z.enum(['P', 'R', 'N', 'B', 'Q', 'K', 'p', 'r', 'n', 'b', 'q', 'k']),
  z.null()
]);

/**
 * Player schema
 */
export const PlayerSchema = z.object({
  id: z.string(),
  color: z.nativeEnum(ChessColor),
  joinKey: z.string(),
  disconnected: z.boolean(),
  capturedPieces: z.array(z.string()),
  timeLeft: z.number(),
  moves: z.array(z.any()).optional(), // Legacy field
  socketId: z.string().optional()
}).passthrough(); // Allow additional fields

/**
 * Chess game schema
 */
export const ChessGameSchema = z.object({
  id: z.string(),
  gameName: z.string(),
  state: z.string(), // Can be GameState enum values or "player0-wins" etc.
  board: z.array(z.array(ChessPieceSchema)),
  castling: z.string(),
  moveNumber: z.number(),
  autoplay: z.boolean(),
  players: z.array(PlayerSchema),
  currentPlayer: z.string().nullable(),
  lastActivity: z.number(),
  moves: z.array(z.any()).optional(), // Legacy field
  winnerId: z.string().optional().nullable(),
  endReason: z.string().optional().nullable()
}).passthrough(); // Allow additional fields

/**
 * Move schema
 */
export const MoveSchema = z.object({
  from: z.string(),
  to: z.string(),
  promotion: z.enum(['q', 'r', 'b', 'n']).optional()
});

/**
 * Socket event schemas
 */
export const JoinGameDataSchema = z.object({
  gameId: z.string(),
  joinKey: z.string().optional()
});

export const JoinedDataSchema = z.string(); // Server sends back playerId as string

export const MakeMoveDataSchema = z.object({
  gameId: z.string(),
  move: MoveSchema
});

export const GameUpdatedDataSchema = ChessGameSchema;

export const NotifyDataSchema = z.string(); // Notification message

export const ErrorDataSchema = z.string(); // Error message

/**
 * Cookie auth data schema
 */
export const ChessAuthDataSchema = z.object({
  playerId: z.string().optional(),
  joinKey: z.string(),
  color: z.nativeEnum(ChessColor)
});

// ============================================================================
// TypeScript Types (inferred from Zod schemas)
// ============================================================================

export type ChessPiece = z.infer<typeof ChessPieceSchema>;
export type Player = z.infer<typeof PlayerSchema>;
export type ChessGame = z.infer<typeof ChessGameSchema>;
export type Move = z.infer<typeof MoveSchema>;
export type JoinGameData = z.infer<typeof JoinGameDataSchema>;
export type JoinedData = z.infer<typeof JoinedDataSchema>;
export type MakeMoveData = z.infer<typeof MakeMoveDataSchema>;
export type GameUpdatedData = z.infer<typeof GameUpdatedDataSchema>;
export type NotifyData = z.infer<typeof NotifyDataSchema>;
export type ErrorData = z.infer<typeof ErrorDataSchema>;
export type ChessAuthData = z.infer<typeof ChessAuthDataSchema>;

// ============================================================================
// Socket.IO Event Types
// ============================================================================

/**
 * Events that the server can emit to clients
 */
export interface ServerToClientEvents {
  gameUpdated: (game: ChessGame) => void;
  joined: (playerId: string) => void;
  notify: (message: string) => void;
  error: (message: string) => void;
}

/**
 * Events that clients can emit to the server
 */
export interface ClientToServerEvents {
  viewGame: (gameId: string) => void;
  joinGame: (gameId: string, joinKey?: string) => void;
  makeMove: (gameId: string, move: Move) => void;
  resign: (gameId: string) => void;
  checkTime: (gameId: string) => void;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface PlayerTimerProps {
  timeLeft: number;
  isActive: boolean;
}

export interface LegendProps {
  // No props - displays static piece legend
}

export interface CapturedPiecesProps {
  pieces: string[];
  color: ChessColor;
}

export interface MovesProps {
  moves: any[]; // TODO: Define proper move history type
}

export interface GameInfoComponentProps {
  game: ChessGame | null;
}

export interface ChessContainerProps {
  game: ChessGame;
  playerId: string | null;
  makeMove: (from: SquarePosition, to: SquarePosition, piece: ChessPiece) => void;
  playerColor: ChessColor | null;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Square position on the chess board
 */
export interface SquarePosition {
  row: number;
  col: number;
}

/**
 * Algebraic notation (e.g., "e4", "d5")
 */
export type AlgebraicNotation = string;

/**
 * Board state - 8x8 array of pieces
 */
export type BoardState = ChessPiece[][];

/**
 * Castling rights (e.g., "KQkq")
 * K = White kingside, Q = White queenside
 * k = Black kingside, q = Black queenside
 */
export type CastlingRights = string;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Type guard to check if a value is a valid ChessPiece
 */
export function isValidChessPiece(value: unknown): value is ChessPiece {
  try {
    ChessPieceSchema.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard to check if a value is a valid ChessGame
 */
export function isValidChessGame(value: unknown): value is ChessGame {
  try {
    ChessGameSchema.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate and parse chess game data from server
 * @throws ZodError if validation fails
 */
export function validateChessGame(data: unknown): ChessGame {
  return ChessGameSchema.parse(data);
}

/**
 * Validate and parse auth data from cookie
 * @throws ZodError if validation fails
 */
export function validateChessAuthData(data: unknown): ChessAuthData {
  return ChessAuthDataSchema.parse(data);
}
